import { useState, useEffect, useCallback } from 'react';
import { ApiError } from '../types';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { extractErrorMessage } from '../utils/error-handler';

export function useTeamInvitations(teamId: string, organizationId?: string) {
  const [invitations, setInvitations] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  
  const fetchInvitations = useCallback(async (page: number = 1, limit: number = 20, status?: string) => {
    if (!teamId || !organizationId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching team invitations for team: ${teamId} in organization: ${organizationId}`);
      const response = await teamService.getTeamInvitations(teamId, page, limit, status);
      
      setInvitations(response.data);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Error fetching team invitations:', err);
      setError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [teamId, organizationId]);
  
  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);
  
  const createInvitation = async (data: {
    invitee_email: string;
    role_id: string;
    personal_message?: string;
  }) => {
    if (!organizationId) return { success: false, error: { message: 'Organization ID required', code: 'MISSING_ORG_ID', status: 400 } };
    
    try {
      setIsLoading(true);
      // Use organization-level invitation API with team context
      const result = await invitationService.createInvitation(organizationId, {
        email: data.invitee_email,
        role: data.role_id,
        team_id: teamId,
        message: data.personal_message
      });
      await fetchInvitations(); // Refresh list
      return { success: true, data: result };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    invitations,
    pagination,
    isLoading,
    error,
    refreshInvitations: fetchInvitations,
    createInvitation
  };
}

export default useTeamInvitations;